import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus as PlusIcon,
  Smartphone as SmartphoneIcon,
  Wifi as WifiIcon,
  BatteryCharging as ChargingIcon,
  ShieldCheck as ShieldIcon,
  Clock as ClockIcon,
  Monitor as MonitorIcon,
  Archive as ArchiveIcon,
  MoreHorizontal as MoreIcon,
  UserPlus as UserPlusIcon,
  ArrowRightLeft as MoveIcon,
} from 'lucide-react'
import * as Sentry from '@sentry/tanstackstart-react'
import { useDeviceSSE } from '../hooks/useSSE'
import { api, studentApi, type DeviceResponse, type StudentResponse } from '../lib/api'
import { getDeviceStatus } from '../lib/utils'
import { queries } from '../lib/queries'
import { polling } from '../lib/polling'
import { useIsMobile } from '../hooks/useIsMobile'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import DeviceModal from './DeviceModal'
import { DeviceOperationModal } from './device-operation'
import { useSubscription } from '../context/SubscriptionContext'

const getDeviceType = (device: DeviceResponse) => {
  if (device.platform === 'android') return 'Android'
  return 'iOS'
}

const getRelativeTime = (dateString: string | null) => {
  if (!dateString) return 'Never'
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
  return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
}

interface DevicesListClientProps {
  showAddModal?: boolean
}

export function DevicesListClient({ showAddModal = false }: DevicesListClientProps) {
  const queryClient = useQueryClient()
  const { canPerformPaidActions, isLoading: subscriptionLoading } = useSubscription()
  const showSubscriptionRestriction = !subscriptionLoading && !canPerformPaidActions
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null)
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(showAddModal)
  const [addDeviceStudentId, setAddDeviceStudentId] = useState<string | null>(null)
  const isMobile = useIsMobile()
  const { isConnected: sseConnected } = useDeviceSSE()

  // Child management state
  const [showCreateChild, setShowCreateChild] = useState(false)
  const [newChildName, setNewChildName] = useState('')
  const [editingStudentId, setEditingStudentId] = useState<string | null>(null)
  const [editStudentName, setEditStudentName] = useState('')
  const [archiveTarget, setArchiveTarget] = useState<StudentResponse | null>(null)
  const addChildButtonRef = useRef<HTMLButtonElement>(null)
  const renameButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map())

  const { data: devicesData, isLoading: loading, error: queryError, refetch } = useQuery({
    queryKey: queries.devices.list(),
    queryFn: () => api.getDevices(),
    staleTime: 0,
    gcTime: 0,
    refetchInterval: selectedDeviceId ? false : (sseConnected ? polling.deviceListWithSSE : polling.deviceListFallback),
  })
  const devices = devicesData?.devices ?? []
  const students = devicesData?.students ?? []
  const error = queryError?.message ?? null

  // Group devices by student
  const devicesByStudent = new Map<string, DeviceResponse[]>()
  for (const device of devices) {
    const key = device.primaryStudentId
    if (!devicesByStudent.has(key)) devicesByStudent.set(key, [])
    devicesByStudent.get(key)!.push(device)
  }

  // Mutations
  const createChildMutation = useMutation({
    mutationFn: (name: string) => studentApi.create({ name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.devices.all })
      queryClient.invalidateQueries({ queryKey: queries.students.all })
      setShowCreateChild(false)
      setNewChildName('')
      addChildButtonRef.current?.focus()
    },
    onError: (error) => Sentry.captureException(error),
  })

  const renameChildMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => studentApi.update(id, { name }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queries.devices.all })
      queryClient.invalidateQueries({ queryKey: queries.students.all })
      const btnRef = renameButtonRefs.current.get(variables.id)
      setEditingStudentId(null)
      setEditStudentName('')
      requestAnimationFrame(() => btnRef?.focus())
    },
    onError: (error) => Sentry.captureException(error),
  })

  const archiveChildMutation = useMutation({
    mutationFn: (id: string) => studentApi.update(id, { archive: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.devices.all })
      queryClient.invalidateQueries({ queryKey: queries.students.all })
      setArchiveTarget(null)
    },
    onError: (error) => Sentry.captureException(error),
  })

  const reassignMutation = useMutation({
    mutationFn: ({ deviceId, primaryStudentId }: { deviceId: string; primaryStudentId: string }) =>
      api.updateDevice(deviceId, { primaryStudentId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.devices.all })
    },
    onError: (error) => Sentry.captureException(error),
  })

  const prefetchDeviceModalData = async (deviceId: string) => {
    await Promise.all([
      queryClient.prefetchQuery({
        queryKey: queries.devices.detail(deviceId),
        queryFn: () => api.getDevice(deviceId),
      }),
      queryClient.prefetchQuery({
        queryKey: queries.devices.policies(deviceId),
        queryFn: () => api.getDevicePolicies(deviceId),
      }),
    ])
  }

  const handleCloseDeviceModal = () => {
    setSelectedDeviceId(null)
  }

  const handleCloseAddDeviceModal = () => {
    setShowAddDeviceModal(false)
    setAddDeviceStudentId(null)
    queryClient.invalidateQueries({ queryKey: queries.devices.list() })
  }

  const handleOpenAddDevice = (studentId?: string) => {
    if (showSubscriptionRestriction) return
    setAddDeviceStudentId(studentId ?? null)
    setShowAddDeviceModal(true)
  }

  const handleCreateChild = (e: React.FormEvent) => {
    e.preventDefault()
    if (newChildName.trim()) createChildMutation.mutate(newChildName.trim())
  }

  const handleRenameChild = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingStudentId && editStudentName.trim()) {
      renameChildMutation.mutate({ id: editingStudentId, name: editStudentName.trim() })
    }
  }

  const handleCancelCreate = () => {
    setShowCreateChild(false)
    setNewChildName('')
    addChildButtonRef.current?.focus()
  }

  const handleCancelRename = () => {
    const id = editingStudentId
    setEditingStudentId(null)
    setEditStudentName('')
    if (id) {
      requestAnimationFrame(() => renameButtonRefs.current.get(id)?.focus())
    }
  }

  const isBootstrap = (student: StudentResponse) => student.source === 'backfill'

  return (
    <>
      {selectedDeviceId && (
        <DeviceModal deviceId={selectedDeviceId} onClose={handleCloseDeviceModal} />
      )}
      {showAddDeviceModal && !isMobile && (
        <DeviceOperationModal
          mode="install"
          onClose={handleCloseAddDeviceModal}
          preSelectedStudentId={addDeviceStudentId ?? undefined}
        />
      )}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4" role="alert">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-sm font-medium text-red-800">Error loading devices</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
              <button onClick={() => refetch()} className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                Retry
              </button>
            </div>
          </div>
        )}

        {archiveChildMutation.error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm" role="alert">
            {archiveChildMutation.error.message}
          </div>
        )}

        {/* Header with Add Child */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Devices</h1>
            <p className="text-sm text-gray-500 mt-1">Manage your family's devices</p>
          </div>
          <button
            ref={addChildButtonRef}
            onClick={() => setShowCreateChild(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors shadow-sm"
          >
            <UserPlusIcon className="h-4 w-4" />
            Add Child
          </button>
        </div>

        {/* Create child form */}
        {showCreateChild && (
          <form onSubmit={handleCreateChild} onKeyDown={(e) => e.key === 'Escape' && handleCancelCreate()} className="mb-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
            <label htmlFor="new-child-name" className="block text-sm font-medium text-gray-700 mb-2">Child's Name</label>
            <div className="flex gap-3">
              <input
                id="new-child-name"
                type="text"
                value={newChildName}
                onChange={(e) => setNewChildName(e.target.value)}
                placeholder="Enter name"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand"
                autoFocus
              />
              <button
                type="submit"
                disabled={!newChildName.trim() || createChildMutation.isPending}
                className="px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {createChildMutation.isPending ? 'Creating...' : 'Create'}
              </button>
              <button
                type="button"
                onClick={handleCancelCreate}
                className="px-4 py-2 text-gray-600 rounded-lg text-sm hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
            </div>
            {createChildMutation.error && (
              <p className="mt-2 text-sm text-red-600" role="alert">{createChildMutation.error.message}</p>
            )}
          </form>
        )}

        {/* Loading skeleton */}
        {loading && devices.length === 0 && (
          <div className="space-y-6">
            {[1, 2].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-32 mb-4" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                  <div className="bg-white rounded-xl shadow-sm p-4 sm:p-5 min-h-[140px] sm:min-h-[160px] w-full sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)]">
                    <div className="flex justify-between items-start mb-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-lg" />
                      <div className="w-14 h-4 bg-gray-200 rounded-full" />
                    </div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/2 mb-4" />
                    <div className="h-3 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state — no students */}
        {!loading && students.length === 0 && devices.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="w-16 h-16 rounded-full bg-brand-light flex items-center justify-center mx-auto mb-4">
              <SmartphoneIcon className="h-8 w-8 text-brand" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Add your first child to get started</h2>
            <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
              Add a child, then set up their devices to manage screen time and content.
            </p>
            <button
              onClick={() => setShowCreateChild(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand text-white rounded-lg text-sm font-medium hover:bg-brand-hover transition-colors"
            >
              <UserPlusIcon className="h-4 w-4" />
              Add Child
            </button>
          </div>
        )}

        {/* Kid-grouped device sections */}
        {!loading && students.length > 0 && (
          <div className="space-y-8">
            {students.map((student) => {
              const studentDevices = devicesByStudent.get(student.id) ?? []
              return (
                <section key={student.id} aria-label={`${student.name}'s devices`}>
                  {/* Student header */}
                  <div className="flex items-center gap-3 mb-4">
                    {editingStudentId === student.id ? (
                      <form onSubmit={handleRenameChild} onKeyDown={(e) => e.key === 'Escape' && handleCancelRename()} className="flex items-center gap-2">
                        <label htmlFor={`rename-child-${student.id}`} className="sr-only">Child's name</label>
                        <input
                          id={`rename-child-${student.id}`}
                          type="text"
                          value={editStudentName}
                          onChange={(e) => setEditStudentName(e.target.value)}
                          className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-base font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand"
                          autoFocus
                        />
                        <button
                          type="submit"
                          disabled={!editStudentName.trim() || renameChildMutation.isPending}
                          className="px-3 py-1.5 text-sm text-brand hover:text-brand-hover font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {renameChildMutation.isPending ? 'Saving...' : 'Save'}
                        </button>
                        <button type="button" onClick={handleCancelRename} className="px-3 py-2.5 text-sm text-gray-500 hover:text-gray-700 min-h-[44px]">Cancel</button>
                      </form>
                    ) : (
                      <>
                        <h2 className="text-lg font-semibold text-gray-900">
                          {student.name}
                        </h2>
                        {isBootstrap(student) && (
                          <button
                            onClick={() => { setEditingStudentId(student.id); setEditStudentName(student.name) }}
                            className="text-xs text-gray-400 hover:text-brand transition-colors"
                          >
                            Set name
                          </button>
                        )}
                        <div className="flex items-center gap-1">
                          <button
                            ref={(el) => { if (el) renameButtonRefs.current.set(student.id, el) }}
                            onClick={() => { setEditingStudentId(student.id); setEditStudentName(student.name) }}
                            className="px-3 py-2.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] flex items-center"
                          >
                            Rename
                          </button>
                          <DropdownMenu>
                            <DropdownMenuTrigger
                              className="p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                              aria-label={`More actions for ${student.name}`}
                            >
                              <MoreIcon className="h-4 w-4" />
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                onClick={() => setArchiveTarget(student)}
                              >
                                <ArchiveIcon className="h-4 w-4 mr-2" />
                                Archive
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Device cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                    {studentDevices.map((device) => {
                      const status = getDeviceStatus(device.lastSeen, device.isEnrolled)
                      const isOnline = status === 'online'
                      const isConnected = device.isConnected === true
                      const otherStudents = students.filter((s) => s.id !== student.id)
                      return (
                        <div
                          key={device.id}
                          onClick={() => setSelectedDeviceId(device.id)}
                          onMouseEnter={() => void prefetchDeviceModalData(device.id)}
                          onFocus={() => void prefetchDeviceModalData(device.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedDeviceId(device.id) } }}
                          className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 hover:shadow-md active:shadow-sm transition-shadow cursor-pointer text-left min-h-[140px] sm:min-h-[160px] flex flex-col"
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                              <SmartphoneIcon size={20} className="text-gray-400" />
                            </div>
                            <div className="flex items-center gap-1">
                              {isOnline && (
                                <>
                                  <WifiIcon size={14} className="text-status-online" />
                                  <span className="text-xs font-medium text-status-online">Online</span>
                                </>
                              )}
                              {!isOnline && isConnected && device.platform !== 'android' && (
                                <>
                                  <ChargingIcon size={14} className="text-blue-600" />
                                  <span className="text-xs font-medium text-blue-600">Connected</span>
                                </>
                              )}
                              {otherStudents.length > 0 && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger
                                    onClick={(e) => e.stopPropagation()}
                                    className="ml-1 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                    aria-label={`Actions for ${device.name || 'device'}`}
                                  >
                                    <MoreIcon className="h-4 w-4" />
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                    <DropdownMenuSub>
                                      <DropdownMenuSubTrigger>
                                        <MoveIcon className="h-4 w-4 mr-2" />
                                        Move to
                                      </DropdownMenuSubTrigger>
                                      <DropdownMenuSubContent>
                                        {otherStudents.map((s) => (
                                          <DropdownMenuItem
                                            key={s.id}
                                            disabled={reassignMutation.isPending}
                                            onClick={() => reassignMutation.mutate({ deviceId: device.id, primaryStudentId: s.id })}
                                          >
                                            {s.name}
                                          </DropdownMenuItem>
                                        ))}
                                      </DropdownMenuSubContent>
                                    </DropdownMenuSub>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          </div>
                          <h3 className="text-base font-semibold text-gray-900 mb-0.5">{device.name || 'Unnamed Device'}</h3>
                          <p className="text-sm text-gray-500 mb-2">{getDeviceType(device)}</p>
                          <div className="flex flex-wrap gap-1.5 mb-auto">
                            {(device.policyCount ?? 0) > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700" title={device.policyName || 'Policy applied'}>
                                <ShieldIcon size={12} className="text-indigo-500" />
                                {device.policyName && device.policyName.length > 12 ? `${device.policyName.substring(0, 12)}...` : device.policyName || 'Policy'}
                                {(device.policyCount ?? 0) > 1 && <span className="ml-0.5 text-indigo-400">+{(device.policyCount ?? 0) - 1}</span>}
                              </span>
                            )}
                            {(device.scheduleCount ?? 0) > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700" title={device.scheduleName || 'Schedule active'}>
                                <ClockIcon size={12} className="text-amber-500" />
                                {device.scheduleName && device.scheduleName.length > 12 ? `${device.scheduleName.substring(0, 12)}...` : device.scheduleName || 'Schedule'}
                                {(device.scheduleCount ?? 0) > 1 && <span className="ml-0.5 text-amber-400">+{(device.scheduleCount ?? 0) - 1}</span>}
                              </span>
                            )}
                          </div>
                          <div className="pt-3 mt-3 border-t border-gray-100">
                            <p className="text-xs text-gray-500">Last active: {getRelativeTime(device.lastSeen)}</p>
                          </div>
                        </div>
                      )
                    })}

                    {/* Add device card for this kid */}
                    {isMobile ? (
                      <div className="bg-white rounded-xl shadow-sm border-dashed border-2 border-gray-200 p-4 sm:p-5 flex flex-col items-center justify-center min-h-[140px] sm:min-h-[160px]">
                        <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-3">
                          <MonitorIcon size={32} className="text-gray-400" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 mb-1">Add Device</h3>
                        <p className="text-xs text-gray-500 text-center px-2">You can only add devices on desktop</p>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleOpenAddDevice(student.id)}
                        disabled={showSubscriptionRestriction}
                        title={showSubscriptionRestriction ? 'Active subscription required' : undefined}
                        className="bg-white rounded-xl shadow-sm border-dashed border-2 border-gray-200 p-4 sm:p-5 hover:shadow-md active:shadow-sm transition-shadow cursor-pointer flex flex-col items-center justify-center min-h-[140px] sm:min-h-[160px] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-sm"
                      >
                        <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-3">
                          <PlusIcon size={32} className="text-gray-400" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900">Add Device</h3>
                        {showSubscriptionRestriction && (
                          <p className="text-xs text-amber-700 mt-1">Reactivate to add devices</p>
                        )}
                      </button>
                    )}
                  </div>
                </section>
              )
            })}
          </div>
        )}
      </div>

      {/* Archive confirmation dialog */}
      <AlertDialog open={!!archiveTarget} onOpenChange={(open) => !open && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive child?</AlertDialogTitle>
            <AlertDialogDescription>
              {archiveTarget && (devicesByStudent.get(archiveTarget.id)?.length ?? 0) > 0
                ? `"${archiveTarget.name}" still has ${devicesByStudent.get(archiveTarget.id)!.length} device(s). Reassign them to another child before archiving.`
                : `Are you sure you want to archive "${archiveTarget?.name}"? This cannot be undone.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 transition-colors text-white disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!!archiveTarget && (devicesByStudent.get(archiveTarget.id)?.length ?? 0) > 0}
              onClick={() => {
                if (archiveTarget) archiveChildMutation.mutate(archiveTarget.id)
              }}
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
